import React from 'react';
import { Segment, Header, Table, Label } from 'semantic-ui-react';
import { DriverRanking } from '../../models/client-ranking';
import { formatDriverPrizeCurrency } from '../../shared/utils/driver-prize';
import './RankingTable.css';

interface RankingTableProps {
  title: string;
  badge: string;
  badgeType: 'best' | 'worst';
  metric: string;
  data: DriverRanking[];
  valueKey: 'inertiaPercentage' | 'motorLoadPercentage';
  prizeKey: 'inertiaPrize' | 'engineLoadPrize';
  onDriverClick?: (driver: DriverRanking) => void;
}

const RankingTable: React.FC<RankingTableProps> = ({
  title,
  badge,
  badgeType,
  metric,
  data,
  valueKey,
  prizeKey,
  onDriverClick,
}) => {
  const formatCpf = (cpf: string) => {
    const cleaned = cpf.replace(/\D/g, '');
    if (cleaned.length !== 11) return cpf;
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const getPositionClass = (position: number) => {
    if (position === 1) return 'gold';
    if (position === 2) return 'silver';
    if (position === 3) return 'bronze';
    return 'default';
  };

  return (
    <Segment className="ranking-card">
      <div className="ranking-header">
        <div className="ranking-title">
          <Header as="h3">{title}</Header>
          <Label className={`ranking-badge ${badgeType}`}>{badge}</Label>
        </div>
        <span className="ranking-metric">{metric}</span>
      </div>

      {data.length === 0 ? (
        <div className="ranking-empty">
          <p>Nenhum dado disponível para o período selecionado.</p>
        </div>
      ) : (
        <Table basic="very" className="ranking-table">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={1}>#</Table.HeaderCell>
              <Table.HeaderCell>Motorista</Table.HeaderCell>
              <Table.HeaderCell width={2}>Placa</Table.HeaderCell>
              <Table.HeaderCell width={3}>{badge}</Table.HeaderCell>
              <Table.HeaderCell width={2}>Prêmio</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data.map((driver, index) => (
              <Table.Row
                key={driver.driverId || `${driver.driverCpf}-${index}`}
                onClick={() => onDriverClick && onDriverClick(driver)}
                className={onDriverClick ? 'clickable-row' : ''}
              >
                <Table.Cell>
                  <span className={`position ${getPositionClass(index + 1)}`}>
                    {index + 1}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <div className="driver-info">
                    <span className="driver-name">{driver.driverName}</span>
                    <span className="driver-cpf">{formatCpf(driver.driverCpf)}</span>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <span className="plate-badge">{driver.plate}</span>
                </Table.Cell>
                <Table.Cell>
                  <div className="percentage-cell">
                    <div className="percentage-bar">
                      <div
                        className={`percentage-fill ${badgeType === 'best' ? 'green' : 'orange'}`}
                        style={{ width: `${Math.min(driver[valueKey], 100)}%` }}
                      />
                    </div>
                    <span className="percentage-value">
                      {Number(driver[valueKey]).toFixed(1)}%
                    </span>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <span className="prize-value">{formatDriverPrizeCurrency(Number(driver[prizeKey] ?? 0))}</span>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </Segment>
  );
};

export default RankingTable;
